import { listLibraryItems } from '@/lib/api/generated/api';
import type { LibraryItemView } from '@/lib/api/generated/model';

import { libraryKeys } from './queryKeys';

/** What the library answers: its items, or that it could not be reached. */
export type CatalogAnswer =
  | { items: LibraryItemView[]; unavailable: false }
  | { items: []; unavailable: true };

/**
 * The one way the catalog is read, for every surface that reads it.
 *
 * There are two: the Library page, and the board's warning that its workflow has
 * lost a part. They share `libraryKeys.items(workspaceId)` — correctly, since it
 * is the same question about the same workspace — and for a while they each had
 * their own `queryFn`, one storing this envelope and the other storing a bare
 * array.
 *
 * That is a crash, not an inefficiency. Two queries under one key share one
 * cache entry, so whichever ran last decided the shape, and the other then read
 * something it was not written for. Visiting the Library and then opening a
 * board did exactly that: the board found an object where it expected an array,
 * called `.find` on it, and took the page down with "Something went wrong".
 *
 * So the function lives here, once, and callers choose only how fresh they need
 * the answer to be. A shape cannot drift if there is only one place that writes
 * it.
 */
export function catalogQuery(workspaceId: string) {
  return {
    queryKey: libraryKeys.items(workspaceId),
    queryFn: async (): Promise<CatalogAnswer> => {
      const response = await listLibraryItems(workspaceId);
      if (response.status === 200) {
        return { items: response.data.items, unavailable: false };
      }
      // A 503 is an answer, not a failure to be retried into oblivion: the
      // Library shows "unavailable" with a way to try again, which is not what
      // an empty library looks like (spec 104 FR-006).
      if (response.status === 503) return { items: [], unavailable: true };
      throw new Error('The library could not be read.');
    },
    retry: false as const,
  };
}
